import { format, getISOWeek, getMonth } from "date-fns";

import { RoomUsageResponse } from "@classview/shared-types";

import { Timeframe } from "../types/timeframe";

type GroupedRoomUsage = {
  label: string;
  count: number;
};

const groupByDay = (roomUsage: RoomUsageResponse): GroupedRoomUsage[] => {
  return roomUsage.map((room) => ({
    label: format(room.day, "MMM dd"),
    count: room.count,
  }));
};

const groupByWeek = (roomUsage: RoomUsageResponse): GroupedRoomUsage[] => {
  const roomUsageByWeek = roomUsage.reduce<Record<number, GroupedRoomUsage>>(
    (prev, roomUsage) => {
      const weekIndex = getISOWeek(new Date(roomUsage.day));
      if (!prev[weekIndex]) {
        prev[weekIndex] = {
          label: `Week ${weekIndex}`,
          count: 0,
        };
      }

      prev[weekIndex].count += roomUsage.count;
      return prev;
    },
    {},
  );

  return Object.values(roomUsageByWeek);
};

const groupByMonth = (roomUsage: RoomUsageResponse): GroupedRoomUsage[] => {
  const roomUsageByMonth = roomUsage.reduce<Record<number, GroupedRoomUsage>>(
    (prev, roomUsage) => {
      const monthIndex = getMonth(new Date(roomUsage.day));
      if (!prev[monthIndex]) {
        prev[monthIndex] = {
          label: format(new Date(roomUsage.day), "MMMM"),
          count: 0,
        };
      }

      prev[monthIndex].count += roomUsage.count;
      return prev;
    },
    {},
  );
  return Object.values(roomUsageByMonth);
};

/**
 * Group room usage based on the timeframe
 * @param roomUsage - The room usage data
 * @param timeframe - The timeframe to group the data by
 * @returns The grouped data
 */
export const groupRoomUsageBasedOnTimeframe = (
  roomUsage: RoomUsageResponse,
  timeframe: Timeframe,
) => {
  switch (timeframe) {
    case "w":
      return groupByDay(roomUsage);
    case "m":
      return groupByWeek(roomUsage);
    case "6m":
      return groupByMonth(roomUsage);
  }
};
